import { assert, error } from '../app/logger.js';
import { services } from '../app/services.js';
import { friends } from '../data/friends.js';
import { Dialog } from '../controls/dialog.js';
import { listenToEvent } from '../util/listen_to_event.js';

import '../components/trip_options.js';

/**
 * Create a trip dialog.
 */
class CreateTripDialog extends Dialog {
  show(loc) {
    assert(loc, `[CreateTripDialog:show] no loc`);
    assert(friends, `[CreateTripDialog:show] no friends`);

    this.designationElement.textContent = loc.name;
    this.optionsElement.location = loc;
    this.optionsElement.friends = friends;

    this.#hash = '';
    this.#origGroupSize = this.optionsElement.groupSize;
    this.#origActivities = this.optionsElement.activities;
    this.#presumeOptions().catch(e => error(e)); // async

    return super.show();
  }

  hide() {
    let result = super.hide();
    if (result) {
      this.optionsElement.friends = null;
    }
    return result;
  }

  static tagName = 'create-trip-dialog';
  static titleMarkup = `Plan a trip to <span class='designation'></span>`;
  static bodyMarkup = `<trip-options id='new-trip-options'></trip-options>`;
  static buttons = {
    accept: {
      label: 'Schedule',
      default: true,
    },
    cancel: {
      label: 'Cancel',
    },
  };

  static get selectors() {
    return Object.assign({}, super.selectors, {
      Designation: '.designation',
      Options: 'trip-options',
    });
  }

  static get elements() {
    return {
      designationElement: this.selectors.Designation,
      optionsElement: this.selectors.Options,
    };
  }

  checkValidity() {
    return this.optionsElement.checkValidity();
  }

  get data() {
    return this.optionsElement;
  }

  initialize() {
    super.initialize();

    this.optionsElement.noLocation = true;
    this.optionsElement.goingWithStyle = 'autocomplete';
    listenToEvent(this.optionsElement, 'optionchange', () =>
      this.#presumeOptions()
    );
  }

  /**
   * Pre-pick a location (caches weather for chosen dates) and presume
   * activities and group size for the location. Ignore if it's not yet
   * established (unknown) location.
   */
  async #presumeOptions() {
    let locId = this.optionsElement.location.id;
    if (!locId) {
      return;
    }

    assert(
      this.optionsElement.startDate,
      `[CreateTripDialog:presumeOptions] no startDate`
    );
    assert(
      this.optionsElement.duration,
      `[CreateTripDialog:presumeOptions] no duration`
    );

    let hash = `${this.optionsElement.startDate}${this.optionsElement.duration}`;
    if (this.#hash == hash) {
      return;
    }
    this.#hash = hash;

    let data = await services.trip.pickLocation({
      startDate: this.optionsElement.startDate,
      duration: this.optionsElement.duration,
      locId,
    });

    if (this.optionsElement.groupSize == this.#origGroupSize) {
      this.optionsElement.groupSize = data.groupSize;
    }
    if (this.optionsElement.activities == this.#origActivities) {
      this.optionsElement.activities = data.activities;
    }
  }

  #hash;
  #origGroupSize;
  #origActivities;
}

CreateTripDialog.defineCustomElement();

export { CreateTripDialog };
